import fetchApiWithAuth from './fetch';

export type MpProduct = {
  id: number;
  marketplace_auth_id: number;
  marketplace: string;
  external_id: string;
  seller_sku: string;
  barcode: string | null;
  title: string | null;
  brand: string | null;
  article: string | null;
  match_source: string | null;
  matched_at: string | null;
  is_archived: boolean;
  synced_at: string | null;
};

export type MpAuthOption = {
  id: number;
  marketplace: string;
  seller_id?: number | null;
};

async function readError(result: Response, fallback: string): Promise<string> {
  const text = await result.text();
  try {
    const json = JSON.parse(text);
    if (typeof json?.message === 'string') return json.message;
    if (Array.isArray(json?.message)) return json.message.join(', ');
  } catch {
    /* plain text */
  }
  return text || fallback;
}

export async function list(params: {
  marketplace_auth_id?: number;
  linked?: 'linked' | 'unlinked' | 'all';
  archived?: 'active' | 'archived' | 'all';
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: MpProduct[]; total: number }> {
  const qs = new URLSearchParams();
  if (params.marketplace_auth_id != null) {
    qs.set('marketplace_auth_id', String(params.marketplace_auth_id));
  }
  if (params.linked) qs.set('linked', params.linked);
  if (params.archived) qs.set('archived', params.archived);
  if (params.search) qs.set('search', params.search);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const result = await fetchApiWithAuth(`/api/mp-products?${qs}`);
  if (!result.ok) throw new Error(await readError(result, 'Ошибка загрузки'));
  return result.json();
}

export async function stats(marketplace_auth_id?: number) {
  const qs =
    marketplace_auth_id != null
      ? `?marketplace_auth_id=${marketplace_auth_id}`
      : '';
  const result = await fetchApiWithAuth(`/api/mp-products/stats${qs}`);
  if (!result.ok) throw new Error(await readError(result, 'Ошибка статистики'));
  return result.json() as Promise<{
    total: number;
    linked: number;
    unlinked: number;
    archived?: number;
    active?: number;
  }>;
}

export async function listAuths(): Promise<MpAuthOption[]> {
  const result = await fetchApiWithAuth('/api/mp-products/auths');
  if (!result.ok) throw new Error(await readError(result, 'Ошибка auth'));
  return result.json();
}

export async function listForGoods(article: string): Promise<MpProduct[]> {
  const result = await fetchApiWithAuth(
    `/api/mp-products/by-goods/${encodeURIComponent(article)}`,
  );
  if (!result.ok) throw new Error(await readError(result, 'Ошибка связей'));
  return result.json();
}

export async function searchGoods(q: string) {
  const result = await fetchApiWithAuth(
    `/api/mp-products/goods-search?q=${encodeURIComponent(q)}`,
  );
  if (!result.ok) throw new Error(await readError(result, 'Ошибка поиска'));
  return result.json() as Promise<{ article: string; name: string }[]>;
}

export async function link(id: number, article: string) {
  const result = await fetchApiWithAuth(`/api/mp-products/${id}/link`, {
    method: 'PUT',
    body: JSON.stringify({ article }),
  });
  if (!result.ok) throw new Error(await readError(result, 'Ошибка привязки'));
  return result.json();
}

export async function unlink(id: number) {
  const result = await fetchApiWithAuth(`/api/mp-products/${id}/link`, {
    method: 'DELETE',
  });
  if (!result.ok) throw new Error(await readError(result, 'Ошибка отвязки'));
  return result.json();
}

export async function enqueueSync(marketplace_auth_id: number) {
  const result = await fetchApiWithAuth('/api/mp-products/sync', {
    method: 'POST',
    body: JSON.stringify({ marketplace_auth_id }),
  });
  if (!result.ok) throw new Error(await readError(result, 'Ошибка sync'));
  return result.json();
}

export async function enqueueSeed(marketplace_auth_id?: number) {
  const result = await fetchApiWithAuth('/api/mp-products/seed-links', {
    method: 'POST',
    body: JSON.stringify({ marketplace_auth_id }),
  });
  if (!result.ok) throw new Error(await readError(result, 'Ошибка seed'));
  return result.json();
}

export async function enqueueAutoMatch(marketplace_auth_id?: number) {
  const result = await fetchApiWithAuth('/api/mp-products/auto-match', {
    method: 'POST',
    body: JSON.stringify({ marketplace_auth_id }),
  });
  if (!result.ok) throw new Error(await readError(result, 'Ошибка auto-match'));
  return result.json();
}
